import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import './MemberHome.css';

/* The screen behind the "Company tickets" tile: the seat pool per type, and who currently sits
   in which Lab on one of those seats.

   The pool is a pool, not a set of bookings — a seat is event-less until an invite puts it on a
   Lab, and reclaiming one takes it straight back. That is why "open of total" is the headline
   number and the allocations are a list underneath rather than the other way round. */

const CompanyTicketsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reclaiming, setReclaiming] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', '/bravos/company-tickets')
      .then(setData)
      .catch(() => navigate('/member-home'));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const reclaimSeat = (ticketId) => {
    if (reclaiming) return;
    setReclaiming(ticketId);
    setNotice(null);
    apiRequest('POST', '/bravos/reclaim-seat', { ticket_id: ticketId })
      .then(() => { setNotice(t('seatReclaimed')); load(); })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('cancelFailed') })))
      .finally(() => setReclaiming(null));
  };

  const pool = (data && data.pool) || [];
  const allocations = (data && data.allocations) || [];

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="ticket" theme="primary" className="me-2" />
          {t('companyTickets')}
        </Title>

        {data === null ? (
          <span className="auth__spinner" />
        ) : pool.length === 0 ? (
          <p className="text-muted mb-0">{t('noCompanyTickets')}</p>
        ) : (
          <>
            <p className="bravos-q__hint mb-2">{t('companyTicketsNote')}</p>
            {pool.map((ty) => (
              <div key={ty.code} className="bravos-pool">
                <span className="bravos-pool__body">
                  <span className="bravos-pool__name">
                    <span className="bravos-pool__emoji" aria-hidden="true">{ty.emoji || ''}</span>
                    {ty.name}
                  </span>
                  {ty.event_locked && (
                    <span className="bravos-pool__lock">
                      <Icon name="lock" className="me-1" />
                      {ty.restricted_event_name
                        ? t('onlyForEvent', { event: ty.restricted_event_name })
                        : t('notUsableYet')}
                    </span>
                  )}
                </span>
                <span className="bravos-basket__type-count">
                  {t('poolOpenOfTotal', { open: ty.open, total: ty.total })}
                  {ty.allocated > 0 && (
                    <span className="bravos-basket__open"> · {t('poolAllocated', { n: ty.allocated })}</span>
                  )}
                </span>
              </div>
            ))}
          </>
        )}
      </Card>

      {data && (
        <Card>
          <Title my="1" className="mt-2">
            <Icon name="user-add" theme="primary" className="me-2" />
            {t('seatsInUse')}
          </Title>
          {notice && <p className="bravos-cal__notice">{notice}</p>}
          {allocations.length === 0 ? (
            <p className="text-muted mb-0">{t('noSeatsInUse')}</p>
          ) : (
            allocations.map((a) => (
              <div key={a.holder.id} className="bravos-member">
                <div className="bravos-member__row">
                  <span className="bravos-member__body">
                    <span className="bravos-member__name">
                      {a.holder.name || t('lab')}
                    </span>
                  </span>
                </div>
                {a.seats.map((s) => (
                  <div key={s.ticket_id} className="bravos-member__alloc">
                    <span className="bravos-member__alloc-name">
                      {s.event_name || t('lab')}
                      {s.type_name && (
                        <span className="bravos-member__alloc-type"> · {s.type_name}</span>
                      )}
                    </span>
                    <button
                      type="button"
                      className="bravos-member__reclaim"
                      onClick={() => reclaimSeat(s.ticket_id)}
                      disabled={reclaiming === s.ticket_id}
                    >
                      {reclaiming === s.ticket_id ? '…' : t('reclaimSeat')}
                    </button>
                  </div>
                ))}
              </div>
            ))
          )}
        </Card>
      )}
    </Base>
  );
};

export default CompanyTicketsPage;
